import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { interval, Observable, of } from 'rxjs';
import { catchError, map, takeWhile } from 'rxjs/operators';
import { UserService } from '../../../../core/services';
import { SuccessCenterTasksListDto } from '../../../dto';
import { SuccessCenterApiService } from '../../../services/api';
import { CacheService, LoadingService } from '../../../services';
import { ModalNavigationEnum } from '../../../constants';

const GET_SUCCESS_CENTER_TASKS_LOADING_TAG: string = 'get-success-center-tasks';

@UntilDestroy()
@Component({
    selector: 'app-success-center-progress-bar',
    templateUrl: './success-center-progress-bar.component.html',
    styleUrls: ['./success-center-progress-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SuccessCenterProgressBarComponent {
  readonly isBarLoading$: Observable<boolean> = this.loadingService.getIsLoading(GET_SUCCESS_CENTER_TASKS_LOADING_TAG);

  isAdmin: boolean;
  accountType: number;

  levelTitles: string[] = ['Getting Started', 'Growing', 'Advanced', 'Expert'];
  currentProgressPercentage = 0;
  achievementLevel = 0;
  iconLeftPosition = '0%';
  successCenterExpanded: boolean;

  readonly modalNavigationEnum = ModalNavigationEnum;

  private readonly daysPeroidToKeepSCCollapsed: number = 30;
  private readonly key: string = 'success-center-collapsed-expiration';

  constructor(
    private readonly userService: UserService,
    private readonly successCenterApiService: SuccessCenterApiService,
    private readonly cacheService: CacheService,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly loadingService: LoadingService,
  ) {
  }

  ngOnInit(): void {
    this.isAdmin = this.userService.isAdmin();
    this.accountType = this.userService.getUserAccountType();

    if (this.isAdmin && this.accountType === 5) {
      this.calculateAchievementLevel();
    }
  }

  getSectionStyle(sectionNumber: number): { background: string } {
    const sectionStart = (sectionNumber - 1) * 25;
    const sectionEnd = sectionNumber * 25;
    let fillPercentage = 0;

    if (this.currentProgressPercentage >= sectionEnd) {
      fillPercentage = 100;
    } else if (this.currentProgressPercentage <= sectionStart) {
      fillPercentage = 0;
    } else {
      fillPercentage = ((this.currentProgressPercentage - sectionStart) / 25) * 100;
    }

    const sectionColors = ['#99E5E9', '#66D7DF', '#43D1DB', '#00BDC9'];
    const color = sectionColors[sectionNumber - 1];

    const background = `linear-gradient(to right, ${color} ${fillPercentage}%, #A0A4B0 ${fillPercentage}%)`;

    return {
      background: background,
    };
  }

  onSuccessCenterToggle(expanded: boolean) {
    this.successCenterExpanded = expanded;
    if (!expanded) {
      if (this.achievementLevel < 4) {
        const date = new Date();
        date.setDate(date.getDate() + this.daysPeroidToKeepSCCollapsed);
        this.setCollapsedExpirationTime(date.getTime());
      } else {
        this.setCollapsedExpirationTime(1);
      }
    } else {
      this.removeCollapsedExpirationTime();
    }
  }

  setCollapsedExpirationTime(time: number) {
    localStorage.setItem(this.key, time.toString());
  }

  getCollapsedExpirationTime(): number {
    const time = localStorage.getItem(this.key);
    if (time && typeof time ==='string'){
      return Number(time)
    }
  }

  removeCollapsedExpirationTime() {
    localStorage.removeItem(this.key);
  }

  initializeSuccessCenterPanelState() {
    const collapsedExpirationTime=this.getCollapsedExpirationTime();
    if (!collapsedExpirationTime) {
      this.onSuccessCenterToggle(true);
      return;
    }

    if (this.achievementLevel < 4) {
      if (new Date().getTime() > collapsedExpirationTime) {
        this.onSuccessCenterToggle(true);
      }
    } else {
      this.onSuccessCenterToggle(false);
    }
  }

  private startProgressAnimation(): void {
    this.iconLeftPosition = '0%';
    this.currentProgressPercentage = 0;
    this.cdr.detectChanges();

    if (this.achievementLevel > 0) {
      const speed = 0.05;
      const targetProgress = (this.achievementLevel / 4) * 100;
      const totalDuration = targetProgress / speed;

      const intervalTime = 50;
      const steps = totalDuration / intervalTime;
      const increment = targetProgress / steps;

      const tileWidth = 100 / 4;
      const slowingPoint = targetProgress - (tileWidth * 0.5);

      interval(intervalTime).pipe(
        takeWhile(() => this.currentProgressPercentage < targetProgress),
        untilDestroyed(this)
      ).subscribe(() => {
        this.zone.run(() => {
          if (this.currentProgressPercentage < slowingPoint) {
            this.currentProgressPercentage = Math.min(this.currentProgressPercentage + increment, targetProgress);
          } else {
            const remainingDistance = targetProgress - this.currentProgressPercentage;
            const totalSlowingDistance = targetProgress - slowingPoint;
            const adjustedIncrement = increment * (remainingDistance / totalSlowingDistance);
            this.currentProgressPercentage = Math.min(this.currentProgressPercentage + adjustedIncrement, targetProgress);
          }

          this.iconLeftPosition = `${this.currentProgressPercentage}%`;
          this.cdr.detectChanges();
        });
      });
    } else {
      this.currentProgressPercentage = 0;
      this.cdr.detectChanges();
    }
  }

  private getLevelFromApiResponse(level: string): number {
    switch (level.toLowerCase()) {
      case 'none':
        return 0;
      case 'getting started':
        return 1;
      case 'growing':
        return 2;
      case 'advanced':
        return 3;
      case 'expert':
        return 4;
      default:
        return 0;
    }
  }

  private calculateAchievementLevel(): void {
    this.getTasksList()
    .pipe(
      map(tasksList => this.getLevelFromApiResponse(tasksList.level)),
      catchError(error => {
        console.error('Error fetching tasks list:', error);
        return of(0);
      }),
      untilDestroyed(this)
    )
    .subscribe(level => {
      this.achievementLevel = level;
      this.initializeSuccessCenterPanelState();
      setTimeout(() => {
        this.startProgressAnimation();
      }, 400);
    });
  }

  private getTasksList(): Observable<SuccessCenterTasksListDto> {
    return this.loadingService.loadingWrapper(this.cacheService.getCachedObservable(
      'success-center-tasks-list',
      this.successCenterApiService.getTasksList()
    ), GET_SUCCESS_CENTER_TASKS_LOADING_TAG);
  }
}
